﻿
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MediaList } from 'components/media';
import { DateToLocal } from 'components/dates';
import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import './CheckFullResults.css';
import { ShowHide } from 'components/layout';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { NotificationInline } from 'components/notifications';

export function CheckFullResults({ check, templateLayout }) {
    const [structuredData, setStructuredData] = useState([]);
    const [triggerQuestionMap, setTriggerQuestionMap] = useState([]);

    const [numberOfSectionToScrollTo, setNumberOfSectionToScrollTo] = useState(null);
    const sectionToScrollTo = useRef(null);

    useEffect(() => {
        const { structured, questionIdToNumberMap } = buildStructuredAnswers(templateLayout, check.answers);
        setStructuredData(structured);
        setTriggerQuestionMap(questionIdToNumberMap);
    }, [templateLayout, check]);

    useEffect(() => {
        if (numberOfSectionToScrollTo && sectionToScrollTo.current) {
            const offset = 75;
            const elementPosition = sectionToScrollTo.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });

            setNumberOfSectionToScrollTo(null);
        }
    }, [numberOfSectionToScrollTo])

    const flattenStructuredData = (component) => {
        const result = [];

        if ("instances" in component && component.instances) {
            result.push(...component.instances)
            result.push(...component.instances.flatMap(i => i.values.flatMap(v => flattenStructuredData(v))));
        }

        return result;
    }

    const markerData = useMemo(() => {
        if (!structuredData) {
            return [];
        }

        const pages = structuredData;
        const parentComponents = structuredData.flatMap(p => p.components);

        let locations = [...pages, ...parentComponents.flatMap(p => flattenStructuredData(p))]
            .filter(p => p.location);

        if (locations.length) {
            if (check.startGeoLocation) {
                locations = [
                    {
                        sectionNumber: 'Start',
                        location: check.startGeoLocation,
                        clickDisabled: true
                    },
                    ...locations
                ]
            }

            if (check.endGeoLocation) {
                locations = [
                    {
                        sectionNumber: 'End',
                        location: check.endGeoLocation,
                        clickDisabled: true
                    },
                    ...locations
                ]
            }
        }

        return locations;
    }, [structuredData]);

    const mapCenter = useMemo(() => {
        const center = {
            lat: 0,
            lng: 0
        };

        if (markerData?.length) {
            const cooridinates = markerData.map(m => m.location);
            center.lng = (Math.max(...cooridinates.map(c => c.longitude)) + Math.min(...cooridinates.map(c => c.longitude))) / 2;
            center.lat = (Math.max(...cooridinates.map(c => c.latitude)) + Math.min(...cooridinates.map(c => c.latitude))) / 2;
        }

        return center;
    }, [markerData])

    const onMapLoad = useCallback(map => {
        // eslint-disable-next-line
        const bounds = new google.maps.LatLngBounds();

        markerData.forEach((marker) => {
            bounds.extend({ lat: marker.location.latitude, lng: marker.location.longitude });
        })
        map.fitBounds(bounds);
    }, [markerData]);

    const onSectionClickInMap = (sectionNumber) => setNumberOfSectionToScrollTo(sectionNumber);

    function flattenComponentTree(comp) {
        const children = comp.childComponents || [];
        return [comp, ...children.flatMap(flattenComponentTree)];
    }

    function getTriggerQuestionNumber(triggerQuestionId, currentRepeatablePath) {
        let currentPath = [];

        while (currentPath.length <= currentRepeatablePath.length) {
            const pathId = createIdForRepeatablePath(currentPath);

            const recordForTriggerQuestion = triggerQuestionMap.find(q => q.repeatablePathId === pathId && q.questionId === triggerQuestionId);

            if (recordForTriggerQuestion) {
                return recordForTriggerQuestion.questionNumber;
            }

            if (currentPath.length < currentRepeatablePath.length) {
                currentPath.push(currentRepeatablePath[currentPath.length]);
            } else {
                return null;
            }
        }

        return null;
    }

    function createIdForRepeatablePath(repeatablePath) {
        if (!repeatablePath) {
            return "";
        }

        return repeatablePath.map(p => `compId:${p.containerId}|iteration:${p.iterationIndex}`).join(',');
    }

    function buildStructuredAnswers(layout, answers) {
        let questionNumber = 1;
        const questionIdToNumberMap = [];

        const findAnswer = (questionId, answerSet) => {
            if (!Array.isArray(answerSet)) return undefined;

            return answerSet.find(ans => ans.questionId === questionId && ans.$answerType !== 'Repeatable');
        }

        const findRepeatableAnswers = (id, answerSet, isContainer) => {
            if (!Array.isArray(answerSet)) return undefined;

            return answerSet.filter(ans =>
                (isContainer ? ans.containerId : ans.questionId) === id && ans.$answerType === 'Repeatable'
            );
        }

        const flattenAnswer = (answer) => {
            if (answer.$answerType === 'Repeatable') {
                return answer.answers.map(a => flattenAnswer(a))
                    .flat();
            }

            return [answer];
        }

        const getLastAnswerMatchingCondition = (answerSet, component, conditionCallback) => {
            const allQuestions = flattenComponentTree(component)
                .filter(x => x.$componentType?.includes('Question'))
                .map(x => x.id);

            const allAnswers = answerSet.map(a => flattenAnswer(a))
                .flat()
                .filter(a => allQuestions.some(q => q === a.questionId) && conditionCallback(a))
                .sort((a, b) => {
                    if (!a.timestamp) return 1;
                    if (!b.timestamp) return -1;
                    return new Date(b.timestamp) - new Date(a.timestamp);
                });

            if (!allAnswers.length) {
                return null;
            }

            return allAnswers[0];
        }

        const getLastAnswerWithLocation = (answerSet, component) => {
            const lastAnswer = getLastAnswerMatchingCondition(answerSet, component, (a) => a.geoLocation);

            if (!lastAnswer) {
                return null;
            }

            return {
                ...lastAnswer.geoLocation,
                timestamp: lastAnswer.timestamp
            }
        }

        const getTimestampOfLastAnswer = (answerSet, component) => {
            const lastAnswer = getLastAnswerMatchingCondition(answerSet, component, (a) => a.timestamp);

            if (!lastAnswer) {
                return null;
            }

            return lastAnswer.timestamp;
        }

        const buildComponents = (components, answerSet, sectionNumberPrefix, repeatablePath) => {
            return components.map((comp, compIndex) => {
                if (comp.$componentType.includes('Container')) {
                    const repeatableContainerAnswers = findRepeatableAnswers(comp.id, answerSet, true);
                    const newSectionNumberPrefix = `${sectionNumberPrefix}.${compIndex + 1}`;

                    let instances;
                    if (repeatableContainerAnswers && repeatableContainerAnswers.length > 0) {
                        instances = repeatableContainerAnswers.map((instance, instanceIndex) => {
                            let repeatableSectionPrefix = `${newSectionNumberPrefix}`
                            if (instanceIndex > 0) {
                                repeatableSectionPrefix = `${repeatableSectionPrefix}-${instanceIndex}`
                            }

                            const newRepeatablePath = [...repeatablePath, { containerId: comp.id, iterationIndex: instanceIndex }]
                            return {
                                sectionNumber: repeatableSectionPrefix,
                                values: buildComponents(comp.childComponents, instance.answers, repeatableSectionPrefix, newRepeatablePath),
                                location: comp.recordLocation ? getLastAnswerWithLocation(instance.answers, comp) : null,
                                timestamp: comp.recordTimestamp ? getTimestampOfLastAnswer(instance.answers, comp) : null,
                                repeatablePath
                            }
                        });
                    } else {
                        if (!Array.isArray(comp.childComponents)) comp.childComponents = [];

                        instances = [{
                            sectionNumber: newSectionNumberPrefix,
                            values: buildComponents(comp.childComponents, answerSet, newSectionNumberPrefix, repeatablePath),
                            location: comp.recordLocation ? getLastAnswerWithLocation(answerSet, comp) : null,
                            timestamp: comp.recordTimestamp ? getTimestampOfLastAnswer(answerSet, comp) : null,
                            repeatablePath
                        }];
                    }

                    return {
                        type: 'section',
                        title: comp.title,
                        repeatable: !!(repeatableContainerAnswers && repeatableContainerAnswers.length > 0),
                        condition: {
                            isConditional: !!(comp.condition?.triggerQuestionId),
                            triggerQuestionId: comp.condition?.triggerQuestionId || null
                        },
                        instances,
                        repeatablePath,
                        sectionNumber: newSectionNumberPrefix
                    };
                } else {
                    const qNum = questionNumber;
                    const repeatableQuestionAnswers = findRepeatableAnswers(comp.id, answerSet, false);
                    questionIdToNumberMap.push({ repeatablePathId: createIdForRepeatablePath(repeatablePath), questionNumber: qNum, questionId: comp.id })
                    questionNumber++

                    if (repeatableQuestionAnswers && repeatableQuestionAnswers.length > 0) {

                        const flatAnswers = repeatableQuestionAnswers.flatMap(r => r.answers.map(a => renderAnswer(a, comp)));
                        return {
                            type: 'question',
                            questionType: comp.$componentType,
                            questionId: comp.id,
                            title: comp.title,
                            questionNumber: qNum,
                            required: comp.required,
                            repeatable: true,
                            condition: {
                                isConditional: !!(comp.condition?.triggerQuestionId),
                                triggerQuestionId: comp.condition?.triggerQuestionId || null
                            },
                            repeatablePath: repeatablePath,
                            answers: flatAnswers
                        };
                    } else {
                        const answer = findAnswer(comp.id, answerSet);
                        return {
                            type: 'question',
                            questionId: comp.id,
                            questionType: comp.$componentType,
                            title: comp.title,
                            questionNumber: qNum,
                            required: comp.required,
                            repeatable: false,
                            condition: {
                                isConditional: !!(comp.condition?.triggerQuestionId),
                                triggerQuestionId: comp.condition?.triggerQuestionId || null
                            },
                            repeatablePath: repeatablePath,
                            answers: [renderAnswer(answer, comp)],
                        };
                    }
                }
            });
        };

        const renderAnswer = (answer, question) => {

            if (!answer) return { text: 'No answer', type: 'Missing' };

            let friendlyAnswerType = answer.$answerType;

            let assetLookupIdentifier = 'No answer';
            if (answer?.answer?.identifier) {
                assetLookupIdentifier = answer.answer.identifier;
                if (answer?.answer && !answer.answer.assetExists) {
                    assetLookupIdentifier += ' (asset does not exist)';
                }
            }

            switch (answer.$answerType) {
                case 'Text':
                case 'Integer':
                case 'Decimal':
                    return {
                        text: answer.answer || 'No answer',
                        type: friendlyAnswerType
                    }
                case 'Date':
                    return {
                        text: answer.answer ? <DateToLocal format='DD/MM/YYYY HH:mm'>{answer.answer}</DateToLocal> : 'No answer',
                        type: friendlyAnswerType
                    }
                case 'YesNo':
                    return {
                        text: answer.answer ? question?.labelForYes : question?.labelForNo,
                        type: "Yes/No"
                    }
                case 'Dropdown': {
                    const allComponents = layout.pages.flatMap(page =>
                        (page.childComponents || []).flatMap(flattenComponentTree)
                    );

                    const dropdownComp = allComponents.find(
                        comp => comp.$componentType === 'DropdownQuestion' && comp.id === answer.questionId
                    );

                    const selectedOption = dropdownComp?.options?.find(opt => opt.id === answer.answer);

                    return {
                        text: selectedOption?.name || `Unknown option (${answer.answer})`,
                        type: "Dropdown"
                    };
                }
                case 'MultiSelect': {
                    const allMultiComponents = layout.pages.flatMap(page =>
                        (page.childComponents || []).flatMap(flattenComponentTree)
                    );

                    const multiSelectComp = allMultiComponents.find(
                        comp => comp.$componentType === 'MultiSelectQuestion' && comp.id === answer.questionId
                    );

                    if (!multiSelectComp || !Array.isArray(answer.answers)) {
                        return {
                            text: 'No selections',
                            type: 'Multi-select'
                        };
                    }

                    const selectedLabels = answer.answers.map(guid => {
                        const option = multiSelectComp.options.find(opt => opt.id === guid);
                        return option ? option.name : `Unknown option (${guid})`;
                    });

                    return {
                        text: selectedLabels.join(', '),
                        type: 'Multi-select'
                    };
                }
                case 'DefectCapture':
                    return {
                        defects: answer.defects || [],
                        mediaForRandomImageCheck: answer.mediaForRandomImageCheck || [],
                        forRandomImageCheck: answer.forRandomImageCheck,
                        text: "N/A",
                        type: friendlyAnswerType
                    }
                case 'MediaCapture':
                    return {
                        media: answer.media || [],
                        text: "N/A",
                        type: friendlyAnswerType,
                        forRandomImageCheck: answer.forRandomImageCheck,
                        numberOfMissingMedia: answer.numberOfMissingMedia || 0
                    }
                case 'AssetLookup':
                    return {
                        text: assetLookupIdentifier,
                        link: answer.answer.assetExists ? `${window.env.VISION_URL}/#/Fleet/RedirectToProfile?assetId=${answer.answer.assetId || ''}` : null,
                        type: "Asset Lookup",
                    }
                default:
                    return {
                        text: 'Unknown answer type',
                        type: 'Unknown'
                    };
            }
        };

        return {
            structured: layout.pages.map((page, idx) => ({
                type: 'page',
                title: page.title,
                pageNumber: idx + 1,
                sectionNumber: `${idx + 1}`,
                location: page.recordLocation ? getLastAnswerWithLocation(answers, page) : null,
                timestamp: page.recordTimestamp ? getTimestampOfLastAnswer(answers, page) : null,
                components: buildComponents(page.childComponents, answers, `${idx + 1}`, []),
                repeatablePath: []
            })),
            questionIdToNumberMap
        };
    }

    const DefectLabel = ({ answers }) => {
        if (!answers || !answers.length) return <Label text="No defects" color="grey" margin="right" />;

        const hasUnsignedDefects = answers.some(ans => ans.defects?.some(defect => defect.signOffDate == null));
        if (hasUnsignedDefects) return <Label text="Outstanding Defects" color="orange" margin="right" />;

        const hasDefects = answers.some(ans => ans.defects?.length > 0);
        if (hasDefects) return <Label text="All defects signed off" showTick={true} color="teal" margin="right" />;

        return <Label text="No defects" color="grey" margin="right" />;
    };

    const Label = ({ text, color, showTick = false, margin = 'none' }) => {

        const classList = [
            'check-fullresults-label',
            `check-fullresults-label--${color}`,
            `check-fullresults-label--margin-${margin}`
        ].join(' ');

        return (
            <span className={classList}>
                {text}
                {showTick && <Icon icon={IconNames.TICK} size={12} />}
            </span>
        );
    };

    const StructuredView = ({ data }) => (
        <>
            <div>
                {data.map(page => (
                    <Page key={page.pageNumber} page={page} />
                ))}
            </div>
        </>
    );

    const Page = ({ page }) => {
        let sectionIndex = 0;
        return (
            <div className="check-fullresults-page spacer-top" ref={numberOfSectionToScrollTo === page.sectionNumber ? sectionToScrollTo : null}>
                <h2>
                    Page {page.pageNumber}: {page.title}
                    <ShowHide
                        evaluator={page.timestamp}
                        show={<span className='check-page-metadata'> Completed at: <DateToLocal>{page.timestamp}</DateToLocal></span>}
                    />
                </h2>
                {page.components.map((component, idx) => {

                    let key = `${page.pageNumber}-${idx}`;
                    const isSection = component.type === 'section';

                    if (isSection) sectionIndex++;

                    return (
                        <ComponentRenderer key={key} component={component} depth={1} />
                    )
                })}
            </div>
        )
    };

    const sectionHasAnsweredQuestions = (section) => {
        return section.instances.some(instance =>
            instance.values.some(child =>
                child.type === 'question'
                    ? child.answers?.some(ans =>
                        ans.text && ans.text !== 'No answer' && ans.type !== 'Missing'
                    )
                    : child.type === 'section' && sectionHasAnsweredQuestions(child)
            )
        );
    };

    const ComponentRenderer = ({ component, depth, nested = false }) => {

        const questionShouldBeDisplayed = component.type === "question" &&
            (
                component.answers?.some(ans =>
                    ans.type !== 'Missing' && ans.text && ans.text !== 'No answer'
                )
            );

        if (component.type === 'section') {

            if (!sectionHasAnsweredQuestions(component)) {
                return null;
            }

            return component.instances.map((instance, idx) => {
                let key = `section-${idx}`
                const triggerQuestionNumber = component.condition?.isConditional ? getTriggerQuestionNumber(component.condition.triggerQuestionId, component.repeatablePath) : null;
                return (
                    <div key={key} className="check-fullresults-section" ref={numberOfSectionToScrollTo === instance.sectionNumber ? sectionToScrollTo : null}>
                        <div className="bp6-text-large spacer-bottom-small check-fullresults-section-header">
                            <span><strong>{instance.sectionNumber} {component.title}</strong></span>
                            {triggerQuestionNumber && (
                                <Label text={`Conditional on Question ${triggerQuestionNumber}`} margin="right" color="blue" />
                            )}
                            {component.repeatable && (<Label text="Repeatable" color="purple" />)}
                            <ShowHide
                                evaluator={instance.timestamp}
                                show={<span className="check-fullresults-repeatable-instance"> Completed at: <DateToLocal>{instance.timestamp}</DateToLocal></span>}
                            />
                            {component.repeatable && (<span className="check-fullresults-repeatable-instance">(Instance {idx + 1})</span>)}
                        </div>
                        {instance.values.map((child, cIdx) => {
                            let childKey = `section-child-${depth}-${cIdx}`;
                            return (
                                <ComponentRenderer key={childKey} component={child} depth={depth + 1} nested={true} />
                            )
                        })}
                    </div>
                )
            });
        }

        if (component.type === 'question' && questionShouldBeDisplayed) {
            const isRepeatableTextual =
                component.repeatable &&
                !['DefectCaptureQuestion', 'MediaCaptureQuestion'].includes(component.questionType);
            const triggerQuestionNumber = component.condition?.isConditional ? getTriggerQuestionNumber(component.condition.triggerQuestionId, component.repeatablePath) : null;

            if (isRepeatableTextual) {

                const answersToRender = component.answers;

                if (!Array.isArray(answersToRender) || answersToRender.length === 0) {
                    return (
                        <div className="check-fullresults-question card">
                            <div className="bp6-text-large check-fullresults-question-header">
                                <span><strong>Question {component.questionNumber}. </strong>{component.title}</span>
                            </div>
                            <div className="check-fullresults-answer">
                                <div className="check-fullresults-answer-missing">No responses provided</div>
                            </div>
                        </div>
                    );
                }

                return answersToRender.map((ans, i) => {
                    const answerKey = `${component.questionId}-${ans.type}-${i}`;

                    return (
                        <div key={answerKey} className="check-fullresults-question card">
                            <span className="check-fullresults-question-labels">
                                {triggerQuestionNumber && (
                                    <Label text={`Conditional on Question ${triggerQuestionNumber}`} margin="right" color="blue" />
                                )}
                                <Label text="Repeatable" color="purple" />
                                <span className="check-fullresults-repeatable-instance">
                                    (Instance {i + 1})
                                </span>
                            </span>
                            <div className="bp6-text-large check-fullresults-question-header">
                                <span><strong>Question {component.questionNumber}. </strong>{component.title}</span>
                            </div>
                            <div className="check-fullresults-answer">
                                <div className="spacer-bottom-small">
                                    <div className="spacer-bottom-small check-fullresults-answer-type">
                                        <span className="check-fullresults-answer-type">{ans.type} Answer</span>
                                    </div>
                                    <ShowHide
                                        evaluator={!!ans.link}
                                        show={
                                            <a href={ans.link} target='_new'>{ans.text}</a>
                                        }
                                        hide={
                                            <div>{ans.text}</div>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )
                });
            }

            const isDefectCapture = component.questionType === 'DefectCaptureQuestion';

            return (
                <div className={`check-fullresults-question${isDefectCapture ? ' defect-card' : ' card'}`}>
                    <span className="check-fullresults-question-labels">
                        {isDefectCapture && <DefectLabel answers={component.answers} />}
                        {component.repeatable && <Label text="Repeatable" color="purple" margin="right" />}
                        {triggerQuestionNumber && (
                            <Label text={`Conditional on Question ${triggerQuestionNumber}`} color="blue" margin="right" />
                        )}
                        {component.answers.some(a => a.forRandomImageCheck) && (<Label text="Random image check" color="purple" />)}
                    </span>
                    <div className="bp6-text-large check-fullresults-question-header">
                        <span><strong>Question {component.questionNumber}.</strong> {component.title}</span>
                    </div>
                    <div className="check-fullresults-answer">
                        {component.answers.map((ans, i) => {
                            const answerKey = `${component.questionId}-${ans.type}-${i}`;
                            if (ans.type === "DefectCapture") {
                                return <DefectCaptureRenderer key={answerKey} answer={ans} />;
                            } else if (ans.type === "MediaCapture") {
                                return <MediaCaptureRenderer key={answerKey} answer={ans} />;
                            } else {
                                return (
                                    <div key={answerKey} className="check-fullresults-answer-block">
                                        <div className="check-fullresults-answer-type">{ans.type} Answer</div>
                                        <ShowHide
                                            evaluator={!!ans.link}
                                            show={
                                                <a href={ans.link} target='_new'>{ans.text}</a>
                                            }
                                            hide={
                                                <div>{ans.text}</div>
                                            }
                                        />
                                    </div>
                                );
                            }
                        })}
                    </div>
                </div>
            );
        }
        return null;
    };

    const DefectCaptureRenderer = ({ answer }) => {
        const defects = answer.defects || [];
        const mediaForRandomImageCheck = answer.mediaForRandomImageCheck || [];
        if (!defects || defects.length === 0) {
            return <div><div className='check-fullresults-answer-nodefects'>No defects</div>
                <ShowHide
                    evaluator={mediaForRandomImageCheck.length}
                    show={
                        <>
                            <MediaList media={mediaForRandomImageCheck} mediaTitle='Media for random image check' size="small" />
                            <RetentionPolicyNotification media={mediaForRandomImageCheck} />
                        </>
                    }
                />
            </div>;
        }

        return (
            <div>
                {defects.map((defect, idx) => (
                    <div key={`defect-${defect.defectId}`} className="check-fullresults-defect">
                        <div className="check-fullresults-defect-header">
                            <span className="check-fullresults-defect-counter"><strong>Defect {idx + 1}:</strong></span>
                            <div className="check-fullresults-defect-signoff">
                                {defect.signOffDate ? (
                                    <>Defect signed off by {defect.signOffUser} on{' '}
                                        <DateToLocal format='DD/MM/YYYY HH:mm'>{defect.signOffDate}</DateToLocal>
                                    </>
                                ) : (
                                    'Defect not signed off!'
                                )}
                            </div>
                        </div>
                        <div className="check-fullresults-defect-details">
                            <div className="check-fullresults-defect-comment"><strong>Comment:</strong> {defect.defectComment}</div>
                            {defect.rectifiedComment && (<div className='check-fullresults-defect-actiontaken'><strong>Action taken:</strong> {defect.rectifiedComment}</div>)}
                            <div>
                                <MediaList media={defect.media} size="small" mediaTitle='Media' />
                                <RetentionPolicyNotification media={defect.media} />
                                <ShowHide
                                    evaluator={defect.numberOfMissingMedia > 0}
                                    show={<div className="check-fullresults-defect-missingmedia">Media Pending ({defect.numberOfMissingMedia}/{defect.media.length + defect.numberOfMissingMedia})</div>}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const MediaCaptureRenderer = ({ answer }) => {

        const media = answer.media || [];
        const numberOfMissingMedia = answer.numberOfMissingMedia || 0;
        const totalMedia = media.length + numberOfMissingMedia;

        if (totalMedia === 0) {
            return <div>No media</div>;
        }

        return (
            <div>
                <MediaList media={media} size="small" mediaTitle='Media' />
                <RetentionPolicyNotification media={media} />
                <ShowHide
                    evaluator={numberOfMissingMedia > 0}
                    show={<div className="check-fullresults-defect-missingmedia">Media Pending ({numberOfMissingMedia}/{totalMedia})</div>}
                />
            </div>
        );
    };

    const RetentionPolicyNotification = ({ media }) => {
        if (!media || media.length === 0) return null;

        return (
            <NotificationInline
                allowClose={false}
                isShort={true}
                show={media.filter(m => m.retentionPolicyApplied).length > 0}
                text="Media has been removed from this question as it exceeds the Logistics UK retention policy."
                intent="danger"
                additionalStyle={{ marginBottom: '6px' }}
            />
        );
    };

    return <>
        <ShowHide
            evaluator={!!markerData?.length}
            show={<GoogleMap
                onLoad={onMapLoad}
                mapContainerClassName='check-map'
                mapContainerStyle={{
                    width: '100%',
                    height: '400px'
                }}
                center={mapCenter}
                zoom={8}
                options={{
                    mapId: window.env.GOOGLEMAPSOPERATINGLICENCEMAPSTYLE,
                    mapTypeControl: false,
                    zoomControl: false,
                    streetViewControl: false,
                    fullscreenControl: false
                }}
            >
                {markerData.map((marker) => marker.location && (
                    <Marker
                        key={marker.sectionNumber}
                        label={{
                            text: marker.sectionNumber,
                            className: 'check-map-marker',
                            color: '#008CFF',
                            fontWeight: '700'
                        }}
                        position={{ lat: marker.location.latitude, lng: marker.location.longitude }}
                        title={marker.sectionNumber}
                        clickable={!marker.clickDisabled}
                        onClick={() => onSectionClickInMap(marker.sectionNumber)}
                        icon={"/icons/map-markers/008CFF-dot.png"}
                    />
                ))}
            </GoogleMap>}
        />

        <StructuredView data={structuredData} />
    </>;
}
